import { useState, useEffect } from 'react';
import { getAvailableScopes, createToken, getTokens, deleteToken } from '../utils/tokenApi';
import { Shield, Trash2, Eye, EyeOff, Copy } from 'lucide-react';

function AccessTokens() {
  const [tokens, setTokens] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tokenToDelete, setTokenToDelete] = useState(null);

  // Form state
  const [newTokenName, setNewTokenName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState([]);
  const [expiresAt, setExpiresAt] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Newly created token (shown only once)
  const [newlyCreatedToken, setNewlyCreatedToken] = useState(null);
  const [showToken, setShowToken] = useState(false);

  useEffect(() => {
    void loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [scopesData, tokensData] = await Promise.all([
        getAvailableScopes(),
        getTokens(),
      ]);
      setScopes(scopesData);
      setTokens(tokensData || []);
      setError(null);
    } catch (err) {
      setError('Error loading data: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateModal = () => {
    setNewTokenName('');
    setSelectedScopes([]);
    setExpiresAt('');
    setShowCreateModal(true);
    setError(null);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setNewTokenName('');
    setSelectedScopes([]);
    setExpiresAt('');
  };

  const handleCreateToken = async (e) => {
    e.preventDefault();

    if (selectedScopes.length === 0) {
      setError('Please select at least one permission.');
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      setIsSubmitting(true);

      // Calculate expiration date (default 1 year if not set)
      let expirationDate = null;
      if (expiresAt) {
        expirationDate = new Date(expiresAt).toISOString();
      }

      const result = await createToken(newTokenName, selectedScopes, expirationDate);

      setNewlyCreatedToken(result);
      setSuccess(result.message);

      // Close form modal and reload token list
      setShowCreateModal(false);
      await loadData();
    } catch (err) {
      setError('Error creating token: ' + (err.message || 'Unknown error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenDeleteModal = (tokenId, tokenName) => {
    setTokenToDelete({ id: tokenId, name: tokenName });
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setTokenToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!tokenToDelete) return;

    try {
      setError(null);
      setIsSubmitting(true);
      await deleteToken(tokenToDelete.id);
      setSuccess('Token successfully deleted');
      setShowDeleteModal(false);
      setTokenToDelete(null);
      await loadData();
    } catch (err) {
      setError('Error deleting token: ' + (err.message || 'Unknown error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleScopeToggle = (scope) => {
    setSelectedScopes(prev => {
      if (prev.includes(scope)) {
        return prev.filter(s => s !== scope);
      } else {
        return [...prev, scope];
      }
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setSuccess('Token copied to clipboard!');
      setTimeout(() => setSuccess(null), 3000);
    }).catch(() => {
      setError('Error copying to clipboard');
    });
  };

  const closeTokenDisplay = () => {
    setNewlyCreatedToken(null);
    setShowToken(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US') + ' ' + date.toLocaleTimeString('en-US');
  };

  const getScopeLabel = (scope) => {
    const scopeObj = scopes.find(s => s.scope === scope);
    return scopeObj ? scopeObj.description : scope;
  };

  const isTokenExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const getExpiredSince = (expiresAt) => {
    if (!expiresAt) return '';
    const now = new Date();
    const expiredDate = new Date(expiresAt);
    const diffMs = now - expiredDate;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (diffDays > 0) {
      return `${diffDays} day${diffDays === 1 ? '' : 's'}`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours === 1 ? '' : 's'}`;
    } else {
      return 'Less than 1 hour';
    }
  };

  const activeTokens = tokens.filter(token => !isTokenExpired(token.expires_at));
  const expiredTokens = tokens.filter(token => isTokenExpired(token.expires_at))
    .sort((a, b) => new Date(b.expires_at) - new Date(a.expires_at)); // Sort by expiration date descending

  if (loading) {
    return (
      <div className="access-tokens-container">
        <h2>Access Tokens</h2>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="access-tokens-container">
      <div className="page-header">
        <h2>
          <Shield size={24} style={{ marginRight: '10px', verticalAlign: 'middle' }} />
          Access Tokens
        </h2>
        <p className="page-description">
          Manage Personal Access Tokens for programmatic API access,
          e.g., for GitLab CI/CD pipelines to automatically upload releases.
        </p>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Newly created token display */}
      {newlyCreatedToken && (
        <div className="token-created-box">
          <h3>✓ Token successfully created!</h3>
          <p className="warning-text">
            ⚠️ Important: Save this token now. It will only be displayed once!
          </p>
          <div className="token-display">
            <label htmlFor="newly-created-token-input">Token:</label>
            <div className="token-field-group">
              <input
                id="newly-created-token-input"
                type={showToken ? 'text' : 'password'}
                value={newlyCreatedToken.token}
                readOnly
                className="token-input"
              />
              <button
                type="button"
                onClick={() => setShowToken(!showToken)}
                className="btn-icon"
                title={showToken ? 'Hide' : 'Show'}
              >
                {showToken ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
              <button
                type="button"
                onClick={() => copyToClipboard(newlyCreatedToken.token)}
                className="btn-primary"
                title="Copy"
              >
                <Copy size={16} style={{ marginRight: '5px' }} />
                Copy
              </button>
            </div>
          </div>
          <div className="token-info">
            <p><strong>Name:</strong> {newlyCreatedToken.token_info.name}</p>
            <p><strong>Prefix:</strong> {newlyCreatedToken.token_info.token_prefix}...</p>
            <p><strong>Expires:</strong> {formatDate(newlyCreatedToken.token_info.expires_at)}</p>
            <p><strong>Permissions:</strong> {newlyCreatedToken.token_info.scopes.map(s => getScopeLabel(s)).join(', ')}</p>
          </div>
          <button onClick={closeTokenDisplay} className="btn-secondary">
            Close
          </button>
        </div>
      )}

      {/* Create new token button */}
      <button
        onClick={handleOpenCreateModal}
        className="btn-primary"
        style={{ marginBottom: '20px' }}
      >
        + Create New Token
      </button>

      {/* Active Token list */}
      <div className="tokens-list">
        <h3>Active Tokens</h3>
        {activeTokens.length === 0 ? (
          <p style={{ color: '#666', fontStyle: 'italic' }}>No active tokens available.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Expiration Date</th>
                <th>Permissions</th>
                <th>Last Used</th>
                <th style={{ width: '80px', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {activeTokens.map((token) => (
                <tr key={token.id}>
                  <td>
                    <strong>{token.name}</strong>
                    <br />
                    <small style={{ color: '#666', fontFamily: 'monospace' }}>{token.token_prefix}...</small>
                  </td>
                  <td>{formatDate(token.expires_at)}</td>
                  <td>
                    <div className="token-scopes">
                      {token.scopes.map(scope => (
                        <span key={scope} className="scope-badge" title={getScopeLabel(scope)}>
                          {scope}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td>
                    {token.last_used_at ? formatDate(token.last_used_at) : <span style={{ color: '#999' }}>Never</span>}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <button
                      onClick={() => handleOpenDeleteModal(token.id, token.name)}
                      className="btn-delete-icon"
                      title="Delete token"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Expired Token list */}
      {expiredTokens.length > 0 && (
        <div className="tokens-list" style={{ marginTop: '40px' }}>
          <h3>Expired Tokens</h3>
          <table className="data-table expired-tokens-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Expired at</th>
                <th>Expired since</th>
                <th>Permissions</th>
                <th>Last Used</th>
              </tr>
            </thead>
            <tbody>
              {expiredTokens.map((token) => (
                <tr key={token.id}>
                  <td>
                    <strong>{token.name}</strong>
                    <br />
                    <small style={{ color: '#666', fontFamily: 'monospace' }}>{token.token_prefix}...</small>
                  </td>
                  <td>{formatDate(token.expires_at)}</td>
                  <td>
                    <span style={{ color: '#f59e0b', fontWeight: '500' }}>
                      {getExpiredSince(token.expires_at)}
                    </span>
                  </td>
                  <td>
                    <div className="token-scopes">
                      {token.scopes.map(scope => (
                        <span key={scope} className="scope-badge expired-badge" title={getScopeLabel(scope)}>
                          {scope}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td>
                    {token.last_used_at ? formatDate(token.last_used_at) : <span style={{ color: '#999' }}>Never</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Token Modal */}
      {showCreateModal && (
        <div className="user-form-overlay">
          <div className="user-form-container" style={{ maxWidth: '600px' }}>
            <div className="user-form-header">
              <h3>Create New Token</h3>
              <button className="close-btn" onClick={handleCloseCreateModal}>×</button>
            </div>

            <form className="user-form" onSubmit={handleCreateToken}>
              <div className="form-group">
                <label htmlFor="tokenName">Token Name*</label>
                <input
                  type="text"
                  id="tokenName"
                  value={newTokenName}
                  onChange={(e) => setNewTokenName(e.target.value)}
                  placeholder="e.g. GitLab CI - MyApp Uploads"
                  required
                  disabled={isSubmitting}
                />
                <small>A descriptive name to identify the token later</small>
              </div>

              <div className="form-group">
                <label htmlFor="expiresAt">Expiration Date</label>
                <input
                  type="date"
                  id="expiresAt"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  disabled={isSubmitting}
                />
                <small>Leave blank for automatic 1 year validity</small>
              </div>

              <div className="form-group">
                <div style={{ marginBottom: '8px' }}>Permissions* (select at least one)</div>
                <div className="scopes-list">
                  {scopes.map((scopeObj) => (
                    <div key={scopeObj.scope} className="scope-item">
                      <label htmlFor={`scope-${scopeObj.scope}`}>
                        <input
                          id={`scope-${scopeObj.scope}`}
                          type="checkbox"
                          checked={selectedScopes.includes(scopeObj.scope)}
                          onChange={() => handleScopeToggle(scopeObj.scope)}
                          disabled={isSubmitting}
                          aria-label={`${scopeObj.scope}: ${scopeObj.description}`}
                        />
                        <span className="scope-label">
                          <strong>{scopeObj.scope}</strong>
                          <br />
                          <small>{scopeObj.description}</small>
                        </span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={handleCloseCreateModal}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-save"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Creating...' : 'Create Token'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Token Confirmation Modal */}
      {showDeleteModal && tokenToDelete && (
        <div className="user-form-overlay">
          <div className="user-form-container" style={{ maxWidth: '500px' }}>
            <div className="user-form-header">
              <h3>Delete Token</h3>
              <button className="close-btn" onClick={handleCloseDeleteModal}>×</button>
            </div>

            <div className="delete-confirmation">
              <div className="warning-icon">⚠️</div>
              <p className="delete-message">
                Do you really want to delete the token <strong>"{tokenToDelete.name}"</strong>?
              </p>
              <p className="delete-warning">
                This action cannot be undone. All applications using this token
                will immediately lose access to the API.
              </p>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={handleCloseDeleteModal}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-delete"
                  onClick={handleConfirmDelete}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Deleting...' : 'Delete Token'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .access-tokens-container {
          padding: 30px;
          max-width: 1400px;
        }

        .page-header h2 {
          display: flex;
          align-items: center;
          margin-bottom: 10px;
          color: #ffffff;
        }

        .page-description {
          color: #d1d5db;
          margin: 10px 0 30px 0;
          line-height: 1.5;
        }

        .error-message {
          background: #ffebee;
          color: #c62828;
          padding: 15px;
          border-radius: 6px;
          margin-bottom: 20px;
          border-left: 4px solid #c62828;
        }

        .success-message {
          background: #e8f5e9;
          color: #2e7d32;
          padding: 15px;
          border-radius: 6px;
          margin-bottom: 20px;
          border-left: 4px solid #4caf50;
        }

        .token-created-box {
          background: rgba(45, 45, 45, 0.95);
          backdrop-filter: blur(10px);
          border: 2px solid rgba(34, 197, 94, 0.4);
          border-radius: 12px;
          padding: 25px;
          margin-bottom: 30px;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.1);
        }

        .token-created-box h3 {
          color: #86efac;
          margin: 0 0 15px 0;
        }

        .warning-text {
          color: #fbbf24;
          font-weight: 600;
          margin: 10px 0 20px 0;
        }

        .token-display {
          margin: 20px 0;
        }

        .token-display label {
          display: block;
          font-weight: 600;
          margin-bottom: 8px;
          color: #e5e7eb;
        }

        .token-field-group {
          display: flex;
          gap: 10px;
          align-items: center;
        }

        .token-input {
          flex: 1;
          padding: 12px;
          font-family: 'Courier New', monospace;
          font-size: 14px;
          border: 2px solid #4caf50;
          border-radius: 6px;
          background: rgba(31, 41, 55, 0.8);
          color: #e5e7eb;
        }

        .btn-icon {
          padding: 10px 12px;
          background: rgba(31, 41, 55, 0.8);
          border: 1px solid rgba(79, 70, 229, 0.3);
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #e5e7eb;
        }

        .btn-icon:hover {
          background: rgba(79, 70, 229, 0.2);
          border-color: rgba(79, 70, 229, 0.5);
        }

        .token-info {
          background: rgba(31, 41, 55, 0.8);
          padding: 20px;
          border-radius: 6px;
          margin: 20px 0;
          border: 1px solid rgba(79, 70, 229, 0.2);
        }

        .token-info p {
          margin: 8px 0;
          line-height: 1.6;
          color: #e5e7eb;
        }

        .btn-primary {
          padding: 10px 20px;
          background: #1976d2;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: background 0.2s;
          display: inline-flex;
          align-items: center;
        }

        .btn-primary:hover {
          background: #1565c0;
        }

        .btn-primary:disabled {
          background: #bbb;
          cursor: not-allowed;
        }

        .btn-secondary {
          padding: 10px 20px;
          background: #757575;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: background 0.2s;
        }

        .btn-secondary:hover {
          background: #616161;
        }

        .tokens-list {
          margin-top: 40px;
        }

        .tokens-list h3 {
          margin-bottom: 20px;
          color: #ffffff;
          font-size: 1.25rem;
          font-weight: 600;
        }

        .data-table {
          width: 100%;
          border-collapse: collapse;
          background: rgba(45, 45, 45, 0.95);
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
          border: 1px solid rgba(79, 70, 229, 0.2);
        }

        .data-table th {
          background: rgba(31, 41, 55, 0.8);
          padding: 15px;
          text-align: left;
          font-weight: 600;
          color: #9ca3af;
          border-bottom: 1px solid rgba(79, 70, 229, 0.2);
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .data-table td {
          padding: 15px;
          border-bottom: 1px solid rgba(79, 70, 229, 0.1);
          color: #e5e7eb;
        }

        .data-table tbody tr:hover {
          background: rgba(79, 70, 229, 0.1);
        }

        .data-table tbody tr:last-child td {
          border-bottom: none;
        }

        .token-scopes {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }

        .scope-badge {
          display: inline-block;
          background: rgba(59, 130, 246, 0.2);
          color: #93c5fd;
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 12px;
          font-family: 'Courier New', monospace;
          font-weight: 500;
          border: 1px solid rgba(59, 130, 246, 0.3);
        }

        .btn-delete-icon {
          background: transparent;
          border: none;
          color: #d32f2f;
          cursor: pointer;
          padding: 8px;
          border-radius: 4px;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .btn-delete-icon:hover {
          background: #ffebee;
          color: #c62828;
        }

        .scopes-list {
          border: 1px solid rgba(79, 70, 229, 0.2);
          border-radius: 6px;
          padding: 15px;
          max-height: 350px;
          overflow-y: auto;
          background: rgba(31, 41, 55, 0.4);
        }

        .scope-item {
          padding: 12px;
          background: rgba(45, 45, 45, 0.6);
          border-radius: 4px;
          margin-bottom: 10px;
          border: 1px solid rgba(79, 70, 229, 0.2);
          transition: all 0.2s ease;
        }

        .scope-item:hover {
          background: rgba(45, 45, 45, 0.8);
          border-color: rgba(79, 70, 229, 0.4);
        }

        .scope-item:last-child {
          margin-bottom: 0;
        }

        .scope-item label {
          display: flex;
          align-items: flex-start;
          cursor: pointer;
          font-weight: normal;
          margin: 0;
        }

        .scope-item input[type="checkbox"] {
          margin-right: 12px;
          margin-top: 3px;
          cursor: pointer;
          accent-color: #4f46e5;
        }

        .scope-label {
          flex: 1;
        }

        .scope-label strong {
          color: #93c5fd;
          font-family: 'Courier New', monospace;
          font-size: 13px;
        }

        .scope-label small {
          color: #d1d5db;
          line-height: 1.4;
        }

        .form-group small {
          display: block;
          color: #d1d5db;
          margin-top: 6px;
          font-size: 13px;
        }

        .delete-confirmation {
          padding: 20px;
        }

        .warning-icon {
          font-size: 48px;
          text-align: center;
          margin-bottom: 20px;
        }

        .delete-message {
          font-size: 16px;
          text-align: center;
          margin-bottom: 15px;
          color: #e5e7eb;
          line-height: 1.5;
        }

        .delete-message strong {
          color: #fbbf24;
          font-weight: 600;
        }

        .delete-warning {
          font-size: 14px;
          text-align: center;
          color: #d1d5db;
          background: rgba(251, 191, 36, 0.1);
          padding: 15px;
          border-radius: 6px;
          border-left: 3px solid #fbbf24;
          margin-bottom: 25px;
          line-height: 1.6;
        }

        .btn-delete {
          padding: 10px 20px;
          background: #dc2626;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: background 0.2s;
        }

        .btn-delete:hover {
          background: #b91c1c;
        }

        .btn-delete:disabled {
          background: #9ca3af;
          cursor: not-allowed;
        }

        .expired-tokens-table tbody tr {
          opacity: 0.7;
        }

        .expired-tokens-table tbody tr:hover {
          opacity: 0.9;
          background: rgba(239, 68, 68, 0.05);
        }

        .expired-badge {
          background: rgba(239, 68, 68, 0.15);
          color: #fca5a5;
          border: 1px solid rgba(239, 68, 68, 0.3);
        }
      `}</style>
    </div>
  );
}

export default AccessTokens;
